import React from 'react';
import { Box, Chip, Typography, Skeleton } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  LocalShipping as ShippingIcon,
  Nature as EcoIcon,
  AttachMoney as MoneyIcon,
  Speed as SpeedIcon,
  Psychology as AIIcon,
} from '@mui/icons-material';

interface SuggestedQuestion {
  question: string;
  icon: React.ReactNode;
  priority: 'high' | 'medium' | 'low';
  color: string;
}

interface SmartQuestionChipsProps {
  resilienceScore?: number;
  carbonTrend?: number;
  activeDisruptions?: number;
  stockoutRisk?: number;
  leadTimeVariance?: number;
  onQuestionClick: (question: string) => void;
  isLoading?: boolean;
}

/**
 * Smart Question Chips - Dynamic "What you should ask" suggestions
 * 
 * Scans dashboard state and suggests relevant questions users should ask.
 * Removes the burden of prompt engineering from users.
 */
const SmartQuestionChips: React.FC<SmartQuestionChipsProps> = ({
  resilienceScore = 0.75,
  carbonTrend = 0,
  activeDisruptions = 0,
  stockoutRisk = 0,
  leadTimeVariance = 0,
  onQuestionClick,
  isLoading = false,
}) => {
  // Generate smart questions based on current dashboard state
  const generateQuestions = (): SuggestedQuestion[] => {
    const questions: SuggestedQuestion[] = [];

    // High priority: Active disruptions
    if (activeDisruptions > 0) {
      questions.push({
        question: `What's causing my ${activeDisruptions} active disruptions?`,
        icon: <WarningIcon />,
        priority: 'high',
        color: '#EF4444',
      });
      questions.push({
        question: 'How can I minimize disruption impact?',
        icon: <AIIcon />,
        priority: 'high',
        color: '#F97316',
      });
    }

    // Medium priority: Low resilience
    if (resilienceScore < 0.6) {
      questions.push({
        question: 'Why is my supply chain vulnerable?',
        icon: <ShippingIcon />,
        priority: 'medium',
        color: '#F59E0B',
      });
      questions.push({
        question: 'Which suppliers are my biggest risk?',
        icon: <WarningIcon />,
        priority: 'medium',
        color: '#F59E0B',
      });
    }

    // Carbon concerns
    if (carbonTrend > 5) {
      questions.push({
        question: 'Why is my carbon footprint increasing?',
        icon: <EcoIcon />,
        priority: 'medium',
        color: '#22C55E',
      });
    }

    // Stockout risk
    if (stockoutRisk > 20) {
      questions.push({
        question: 'Which products might run out soon?',
        icon: <ShippingIcon />,
        priority: 'high',
        color: '#EF4444',
      });
    }

    // Lead time issues
    if (leadTimeVariance > 15) {
      questions.push({
        question: 'Why are my delivery times unpredictable?',
        icon: <SpeedIcon />,
        priority: 'medium',
        color: '#3B82F6',
      });
    }

    // Always show some helpful questions
    questions.push({
      question: 'How can I reduce costs this month?',
      icon: <MoneyIcon />,
      priority: 'low',
      color: '#10B981',
    });

    questions.push({
      question: 'What should I focus on today?',
      icon: <TrendingUpIcon />,
      priority: 'low',
      color: '#8B5CF6',
    });

    // Sort by priority and limit
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return questions
      .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
      .slice(0, 5);
  };

  const questions = generateQuestions();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" width={200} height={32} sx={{ borderRadius: 4 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        💡 Questions you might want to ask:
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {questions.map((q, index) => (
          <Chip
            key={index}
            icon={<Box sx={{ color: q.color, display: 'flex' }}>{q.icon}</Box>}
            label={q.question}
            onClick={() => onQuestionClick(q.question)}
            sx={{
              cursor: 'pointer',
              border: q.priority === 'high' ? `2px solid ${q.color}` : '1px solid rgba(255,255,255,0.2)',
              background: q.priority === 'high' ? `${q.color}22` : 'rgba(255,255,255,0.05)',
              animation: q.priority === 'high' ? 'pulse-border 2s infinite' : 'none',
              '@keyframes pulse-border': {
                '0%, 100%': { borderColor: q.color },
                '50%': { borderColor: `${q.color}88` },
              },
              '&:hover': {
                background: `${q.color}33`,
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SmartQuestionChips;
